import { uiText, useUiLanguage } from '@/i18n/interface';

import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { confirmSignup } from '@/utils/confirmSignup';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const ConfirmSignup = () => {
  useUiLanguage();
  const initialUrl = useRef(window.location.href);
  const operation = useRef<Promise<void> | null>(null);
  const navigate = useNavigate();
  const [status, setStatus] = useState<'pending' | 'confirming' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    setStatus('confirming');
    // Reuse the operation during StrictMode effect replay: OTPs are single-use.
    operation.current ??= confirmSignup(supabase.auth, initialUrl.current);
    operation.current.then(() => {
      if (!active) return;
      window.history.replaceState(null, '', window.location.pathname);
      setStatus('success');
      timer = setTimeout(() => navigate('/login?message=email-confirmed', { replace: true }), 3000);
    }).catch(() => {
      if (!active) return;
      setStatus('error');
      setErrorMessage(uiText('There was an error confirming your email'));
    });
    return () => { active = false; clearTimeout(timer); };
  }, [navigate]);

  const renderContent = () => {
    switch (status) {
      case 'pending':
        return (
          <Card className="w-full max-w-md mx-auto">
            <CardHeader className="text-center">
              <CardTitle>{uiText("Email Confirmation")}</CardTitle>
              <CardDescription>
                {uiText("Click the button below to confirm your email address")}</CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button onClick={() => navigate('/login')} className="w-full">
                {uiText("Confirm Email")}</Button>
            </CardContent>
          </Card>
        );

      case 'confirming':
        return (
          <Card className="w-full max-w-md mx-auto">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                {uiText("Confirming Email")}</CardTitle>
              <CardDescription>
                {uiText("Please wait while we confirm your email address...")}</CardDescription>
            </CardHeader>
          </Card>
        );

      case 'success':
        return (
          <Card className="w-full max-w-md mx-auto">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                {uiText("Email Confirmed!")}</CardTitle>
              <CardDescription>
                {uiText("Your email has been successfully confirmed. Redirecting you to the login page...")}</CardDescription>
            </CardHeader>
          </Card>
        );

      case 'error':
        return (
          <Card className="w-full max-w-md mx-auto">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                {uiText("Confirmation Failed")}</CardTitle>
              <CardDescription>
                {errorMessage || uiText("There was an error confirming your email")}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                {uiText("If your email is already confirmed, you can proceed to login.")}</p>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full"
              >
                {uiText("Go to Login")}</Button>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="wh-app min-h-screen bg-background from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      {renderContent()}
    </div>
  );
};

export default ConfirmSignup;
